import {
  ALLERGEN_CODES,
  CATALOGUE_AVAILABILITIES,
  CHECKOUT_REFUSAL_CAUSES,
  DELIVERY_CLOSED_OUTCOMES,
  DELIVERY_JOB_STATUSES,
  DIETARY_TAGS,
  DOOR_POSITIONS,
  GUEST_STAGES,
  INCIDENT_CAUSES,
  ORDER_CANCELLED_CAUSES,
  PROVIDER_CAPABILITIES,
  REJECTION_REASON_CODES,
  RESPONSIBLE_PARTIES,
} from '@croissants/domain/vocabulary';
import { z } from 'zod';

/**
 * Wire vocabulary. Shared closed sets come from the pure domain/vocabulary leaf;
 * wire-only states remain local to the schemas that describe them.
 */

/** PRD section 4.2. Guest-visible status is a projection of this, never this. */
/**
 * The PAYMENT region, on the wire (PRD section 4.2).
 *
 * ONE VOCABULARY, the region's own. It said `NONE`, `AUTHORIZED`, `CAPTURED`
 * and `VOIDED` until the drop release, because the wire kept the retired axis
 * spelling while a counter handheld that had not taken the new build was still
 * reading it. Those four names are gone: a region and the wire that carries it
 * saying the same word is what stops a mapping existing at all, and every
 * mapping that existed here was a place two spellings could disagree.
 *
 * The old names, for anyone reading an `order_events` row written before the
 * refactor: `NONE` was CREATED, `AUTHORIZED` PENDING (nothing authorizes any
 * more - capture at checkout, founder 2026-08-31), `CAPTURED` PAID and `VOIDED`
 * ABANDONED.
 *
 * A REFUND IS NOT HERE (register item 51, 2026-09-22; founder rule 11 of
 * 29 September 2026). The five refund states this enum carried -
 * REFUND_REVIEW, REFUNDED, PARTIALLY_REFUNDED, NOT_REFUNDED and CLAIM_OPENED -
 * are gone: a refund is a thin case of its own (`OrderRefunds`,
 * contracts/refunds.ts), opened when the order is final, never a payment phase.
 */
export const PaymentPhase = z.enum([
  /** An intent exists and the guest is still on the card form (PAY-025). */
  'PENDING',
  'PAID',
  /**
   * NO MONEY WAS TAKEN, and there is one name for it (decision 13).
   *
   * `ABANDONED` stood beside this for the intent a guest never completed, and
   * the two were one fact with two words: a declined card, a failed off-session
   * charge and a released intent all mean the platform is holding nothing. The
   * failure REASON is what tells them apart on the finance screen, and a void
   * has stated one since the states merged.
   */
  'FAILED',
]);
export type PaymentPhase = z.infer<typeof PaymentPhase>;

/**
 * The courier job as the wire states it.
 *
 * ## The leg is priced ONCE (decision 13)
 *
 * `ESTIMATED` prices the guest's windows and their receipt: it is taken for the
 * ROUTE at allocation, cached per (shop, apartment), at any lead time, and no
 * courier exists. When the network issued a bookable id with that reading the
 * estimate HOLDS it, and the booking a few minutes later redeems the same id.
 *
 * `QUOTED` and `QUOTE_EXPIRED` stood beside it and are gone. Neither was ever a
 * position an order RESTED in: dispatch took the bookable quote and redeemed it
 * in the same transaction, and an expiry was an attempt that failed, leaving
 * the region where it had been for the next dispatch to ask again. What the
 * pair was for survives on the booking, which records its basis (QUOTE|PRICE)
 * and the id it redeemed - one row rather than three states.
 *
 * `ESTIMATED` is therefore the whole of NO COURIER HAS BEEN ASKED FOR, which is
 * what `NOT_BOOKED` always meant, and every projection here says so by asking
 * `deliveryUnbooked` rather than comparing against a name. That comparison was
 * written at five call sites and each of them would have flipped its answer the
 * day the region started reporting an estimate: a morning three days away would
 * have opened the live tracker, and the counter would have lost More time on
 * every pre-order it holds (the estimate is taken at allocation, so it is true
 * of every order there is).
 *
 * The rest of the region now reaches the wire under ITS OWN names. Five of
 * them used to arrive in the retired axis's spelling - `AT_BAKER` as
 * `COURIER_ARRIVED`, `IN_TRANSIT` as `PICKED_UP`, `COMPLETED` as `DELIVERED`,
 * `NO_COURIER` as `UNAVAILABLE`, `NOT_QUOTED` as `NOT_BOOKED` - so that a
 * counter handheld running the previous build kept working. Each new name says
 * something the old one could not: "arrived" could equally mean the door, and
 * an unavailable courier leaves a re-bookable job while a handover that failed
 * at the door does not.
 */
export const DeliveryPhaseKind = z.enum([
  /** An indicative price and travel time for the ROUTE. No courier exists. */
  'ESTIMATED',
  'BOOKED',
  'COURIER_ASSIGNED',
  /** The rider is AT the counter and the bag is not handed over (BAK-044). */
  'AT_BAKER',
  'IN_TRANSIT',
  'ARRIVING',
  'COMPLETED',
  /** No courier took the job. The bag is on the shelf and it is re-quotable. */
  'NO_COURIER',
  'FAILED',
  /** The delivery failed after collection and the bag is riding back (founder ruling #21). */
  'RETURNING',
  /** The returned bag is back at the counter. */
  'RETURNED',
]);
export type DeliveryPhaseKind = z.infer<typeof DeliveryPhaseKind>;

/**
 * The ORDER region as the wire states it (plan 1.2, PRD 4.2) - ONE serial
 * workflow where `assignment`, `bag` and `lifecycle` were three axes.
 *
 * Those three were never independent coordinates. An order is offered, taken,
 * made, collected and delivered, in that order; modelling one line as a cube
 * made the machine claim 41 reachable combinations of them where 13 positions
 * exist, so every projection had to answer for states no order could occupy.
 * `bakeryQueueOf` reading `assignment` AND `bag` and having to RANK them is
 * that arithmetic surfacing at a call site.
 *
 * ## The four that are EVENTS now, and the one refusal that rests
 *
 * `REFUSED`, `NO_ANSWER`, `EXHAUSTED` and `DELIVERY_FAILED` are gone (founder,
 * 15 September 2026, decisions 8 and 13). Cancellation is automatic - it is
 * written in the same transaction as the event that makes the order
 * unfulfillable - so a missed answer and an exhausted cascade last no time at
 * all: they are followed, in that same write, by the next offer or by the
 * cancellation. They survive where they always belonged, as rows in
 * `order_events`, which is where ALC-005 counts them and where
 * `answerableCounterFor` reads who is answerable for an order nobody made.
 *
 * A REFUSAL RESTS AGAIN, as `AWAITING_ASSIGNMENT` (#2155, decision 4,
 * 2026-10-09: "refuse now, engine moves"). A counter's no - a tap, or a closed
 * day - writes only the refusal, and the every-minute acceptance sweep
 * re-offers the order or ends it. It is not called `REFUSED`, which
 * `RetiredOrderPhase` below still reserves, and no surface shows it as a state
 * (decision 3): the counter has no lane for it, the guest keeps the stage
 * already reached, and Internal reads "awaiting bakery".
 *
 * `DELIVERY_FAILED` was a duplicate of the delivery region's own FAILED: a
 * handover that fails at the door CANCELS the order now, and the cancellation
 * says which ending it is (`cancelledCause`).
 *
 * The names survive elsewhere and must not be deleted with the states:
 * `DELIVERY_FAILED` is an event name, a cancelled cause, an alert kind and a
 * provider failure reason; REFUSED and NO_ANSWER are
 * `BakeryOrderOutcome` members. None of them is an order phase.
 *
 * ## The offer interval is THREE states (BAK-044, founder 2026-09-09)
 *
 * `SCHEDULED`, `ACCEPTANCE_OPEN` and `ACCEPTANCE_CALLING` are the interval in
 * which a counter owes an answer, split by the two facts the machine already
 * owns an instant for: may the counter answer (`acceptanceOpensAt`), and is it
 * being rung (`announceFromAt`). A monotonic two-bit ladder with one impossible
 * corner - `announceFromAt = max(opensAt, deadline - 60 min)`, so rung-but-not-
 * permitted is unreachable by construction. They are entered by
 * `ACCEPTANCE_OPENED` and `ACCEPTANCE_ANNOUNCED`, two timer events raised by
 * `sweepAcceptance` exactly as `ACCEPTANCE_LAPSED` already is.
 *
 * Each earns its place by the rule this whole payload rests on: A STATE EARNS
 * ITS PLACE WHEN THE PAYLOAD DIFFERS. The counter may not answer in the first,
 * may and is silent in the second, and is being rung in the third - so the
 * alert list differs, and one name could not have said which.
 *
 * An IMMEDIATE order lands in `ACCEPTANCE_CALLING` directly and neither timer
 * event ever fires, with no branch on `fulfillmentMode`: its
 * `acceptanceOpensAt` IS the offer instant, so both instants have already
 * passed when the order is allocated.
 *
 * ## `ACCEPTED` is gone
 *
 * It named the transition that entered it (`BAKERY_ACCEPTED`) rather than a
 * condition of the order, and read beside `ACCEPTANCE_OPEN` the fault is plain.
 * `BAKERY_ACCEPTED` now lands the order straight in `PREPARING`, and the same
 * transaction writes the courier booking onto the outbox - which deletes a
 * silent failure a sweep-driven booking allowed: a sweep that did not reach an
 * order booked no courier, with no error and no dead letter
 * (docs/plans/counter-contract-stage-payload.html section 3).
 *
 * It does NOT move the delivery region itself, and an earlier draft of this
 * comment said it did. `BOOKED` means a provider CONFIRMED a job, which is a
 * network call, and no network call may happen inside an open transaction - so
 * the acceptance enqueues and `COURIER_BOOKED` is the one edge into `BOOKED`,
 * applied when the provider answers.
 */
export const OrderPhase = z.enum([
  /** Paid for or not, offered to nobody yet - and it never means anything else. */
  'CREATED',
  /** BAK-044: allocated, and the counter may not answer yet. Silent. */
  'SCHEDULED',
  /** BAK-044: the counter may answer, and is not being rung. Silent. */
  'ACCEPTANCE_OPEN',
  /** BAK-044: the counter may answer and is being rung. */
  'ACCEPTANCE_CALLING',
  'PREPARING',
  'READY',
  /** #2155: refused, and waiting for the engine to re-offer or end it. */
  'AWAITING_ASSIGNMENT',
  'COLLECTED',
  'DELIVERED',
  'CANCELLED',
  'CLOSED',
]);
export type OrderPhase = z.infer<typeof OrderPhase>;

/**
 * THE FOUR NAMES THE ORDER REGION NO LONGER HAS, kept for REQUESTS only and
 * for one release (decision 13; DS-06).
 *
 * `REFUSED`, `NO_ANSWER`, `EXHAUSTED` and `DELIVERY_FAILED` were positions an
 * order could rest in; automatic cancellation (decision 8) means they last no
 * time, so they are events on the history now and the order is CANCELLED with
 * its cause. Narrowing a RESPONSE enum is safe - a frontend never sees a state
 * its own copy cannot hold - and narrowing a REQUEST enum is the dangerous
 * direction: the worker deploys before the frontends, an operator's Orders tab
 * keeps its ticked filters per history entry, and a pasted incident link
 * carries whichever word was live when it was written. So a query may still
 * NAME one of these and the server ignores it, for the release after it was
 * renamed.
 *
 * It is never a response type, never stored, and never written. It goes in the
 * release after the one that narrowed `OrderPhase`.
 */
export const RetiredOrderPhase = z.enum(['REFUSED', 'NO_ANSWER', 'EXHAUSTED', 'DELIVERY_FAILED']);
export type RetiredOrderPhase = z.infer<typeof RetiredOrderPhase>;

/** True when a requested phase is one the machine still has (DS-06). */
export function isLiveOrderPhase(phase: string): phase is OrderPhase {
  return (OrderPhase.options as readonly string[]).includes(phase);
}

/**
 * The seven LIVE positions a cancellation can have overwritten (engineering
 * resolution 25, and #2155).
 *
 * `cancelledFrom` used to be `OrderPhase`, which admitted CREATED, the endings
 * themselves and the three cascade answers that no longer exist - a field whose
 * type could say "cancelled out of CANCELLED". A cancellation from CREATED (the
 * abandoned checkout, the nightly check's no-payment-record void) destroys no
 * position and answers null.
 *
 * `AWAITING_ASSIGNMENT` is the seventh: the engine ends an order nobody else
 * could take out of the refusal it was resting on, and no counter held it then
 * - so the house that refused, or closed its day, is owed no Cancelled tile
 * (`counterHadAccepted`) with no rule of its own.
 */
export const CancelledFromPhase = z.enum([
  'SCHEDULED',
  'ACCEPTANCE_OPEN',
  'ACCEPTANCE_CALLING',
  'PREPARING',
  'READY',
  'AWAITING_ASSIGNMENT',
  'COLLECTED',
]);
export type CancelledFromPhase = z.infer<typeof CancelledFromPhase>;

/**
 * WHY a cancelled order ended, as a closed set (founder decisions of 15-17
 * September 2026, engineering resolution 25).
 *
 * Every cancellation is automatic now, so the three states that used to hold
 * the answer for a few seconds are gone - and with them the only thing that let
 * the guest page say "no bakery could take your order" rather than an ordinary
 * cancellation. This is that answer.
 *
 * TWO DIFFERENT QUESTIONS, and neither stands in for the other: `cancelledFrom`
 * is the POSITION the ending overwrote, and this is WHAT HAPPENED - the ONE
 * cause a cancellation carries (founder, 5 October 2026), from which the case's
 * suggested party is read (`suggestedChargedTo`). Every row of
 * docs/counter-what-happens-if.html and every cancellation path maps to exactly
 * one member (`packages/services/src/refunds/lane-map.test.ts`).
 *
 * The guest's "no bakery could take your order" reads the first two members and
 * nothing else.
 */
export const OrderCancelledCause = z.enum(ORDER_CANCELLED_CAUSES);
export type OrderCancelledCause = z.infer<typeof OrderCancelledCause>;

/**
 * The CLAIM region as the wire states it (plan 1.4, PAY-013, PAY-030).
 *
 * The first region name to reach the wire under its own name during the
 * lifecycle migration, and it can be: there is no legacy spelling of a
 * claim to preserve, because before this it was an `internal.alert` outbox row
 * with a `kind` of `PROVIDER_CLAIM_REQUIRED` and no state at all - a work item
 * that could be raised, and never settled, rejected, escalated or written off.
 */
export const ClaimPhase = z.enum([
  'NONE',
  'OPEN',
  'SETTLED',
  'REJECTED',
  'ESCALATED',
  'WRITTEN_OFF',
]);
export type ClaimPhase = z.infer<typeof ClaimPhase>;

/** What an operator may do to a claim that is already open (plan 1.4). */
export const ClaimAction = z.enum(['SETTLE', 'REJECT', 'ESCALATE', 'WRITE_OFF']);
export type ClaimAction = z.infer<typeof ClaimAction>;

/**
 * An order's whole state on the wire: the four regions, and nothing else
 * (PRD section 4.2).
 *
 * It said it TWICE for four releases - `assignment`, `bag` and `lifecycle`
 * beside the regions, derived from `order` so the two halves could not
 * disagree - so that a counter handheld running an older build kept working
 * while the fleet took the new one. The fleet has it, and the three fields are
 * gone.
 *
 * The counter ticket needs no field of its own for this: `BakeryTicket` already
 * carries this shape, so the handheld reads `ticket.lifecycle.order`. A second
 * top-level copy would be a second place for the same fact to be wrong.
 */
export const OrderLifecycleWire = z.object({
  payment: PaymentPhase,
  /** The serial workflow. Read this, not the three retired axes below it. */
  order: OrderPhase,
  delivery: DeliveryPhaseKind,
  /** The provider claim, on its own clock and outliving the order (plan 1.4). */
  claim: ClaimPhase,
  /**
   * The LIVE position a CANCELLED order was ended out of, and null otherwise
   * (ALC-006, ALC-010).
   *
   * One serial region cannot hold "the order had got as far as the bake" and
   * "the order is cancelled" at once - the retired axes could, on `assignment`
   * and `lifecycle` respectively - so the ending carries what it overwrote.
   */
  cancelledFrom: CancelledFromPhase.nullable(),
  /**
   * WHY it ended (resolution 25), and the field the guest page reads to say "no
   * bakery could take your order" now that the three cascade states are gone.
   *
   * OPTIONAL as well as nullable, and deliberately: the worker deploys before
   * the frontends in the same run, so a bundle an operator's tab is still
   * holding must parse a response that carries it, and one built from this
   * schema must parse a response from a worker that does not. Null is "the
   * previous release wrote this cancellation".
   */
  cancelledCause: OrderCancelledCause.nullable().optional(),
  /**
   * DID THE COUNTER EVER SAY THE BAG WAS READY, on an order the courier is
   * holding (GitHub #1299, BAK-007, OPS-002)?
   *
   * `false` is the one this exists for: the provider reported a pickup while
   * the order was still PREPARING, so the shop's half of the handover is
   * missing and an operator is asked to ring the house (`pickupUndeclared`).
   * `true` is the ordinary bag, declared and then collected.
   *
   * NULL OR ABSENT ON EVERY ORDER THAT IS NOT COLLECTED, because the question
   * only has an answer once the rider has the bag: before the pickup the
   * region's own kind says it (READY against PREPARING), and after it the
   * order has moved on. Also null on a row the previous release wrote, which
   * had no column to hold it.
   *
   * OPTIONAL as well as nullable, for the reason `cancelledCause` above it is:
   * the worker deploys before the frontends in the same run, so a bundle an
   * operator's tab is still holding must parse a response that carries it, and
   * one built from this schema must parse a response from a worker that does
   * not.
   */
  bagDeclared: z.boolean().nullable().optional(),
});
export type OrderLifecycleWire = z.infer<typeof OrderLifecycleWire>;

/**
 * ONE WORD for a human reading a table row (`orderSummaryStateOf`).
 *
 * Read-only by construction: no use-case branches on it, nothing is stored as
 * it, and no gate consults it. That rule is the only thing keeping it from
 * becoming `OrderStatus` again under a new name - a summary that something
 * starts deciding with has stopped being a summary.
 */
export const OrderSummaryState = z.enum([
  'DRAFT',
  'PAYMENT_PENDING',
  'PLACED',
  'AWAITING_BAKERY',
  'PREPARING',
  'READY',
  'FINDING_COURIER',
  'ON_THE_WAY',
  'DELIVERED',
  'CANCELLED',
  'UNALLOCATED',
  'COURIER_UNAVAILABLE',
  'PAYMENT_FAILED',
  'NEEDS_OPERATOR',
]);
export type OrderSummaryState = z.infer<typeof OrderSummaryState>;

/**
 * The normative guest-facing projection (PRD GST-036). Stages only advance: an
 * out-of-order provider event never moves the timeline backwards.
 */
export const GuestStage = z.enum(GUEST_STAGES);
export type GuestStage = z.infer<typeof GuestStage>;

/**
 * What an APARTMENT is sold (PRD OPS-013, GST-028) - three since the founder's
 * decision of 20 September 2026.
 *
 *   A    the curated shelf, house never named (CAT-009, GST-029);
 *   B    the guest picks a named house off the chooser (GST-030);
 *   B2   the platform picks the CLOSEST mode B shop at every scan; no chooser,
 *        no name, no logo, no house anywhere on the wire.
 *
 * Who chooses and who is named are `guestChoosesShop` / `namesTheShop` in
 * `./projections/storefront-modes`; which POOL of houses a mode buys from is
 * `bakeryPoolOf` in `@croissants/domain`. A `=== 'B'` that is really asking one
 * of those three questions is the defect this split exists to make impossible.
 */
export const StorefrontMode = z.enum(['A', 'B', 'B2']);
export type StorefrontMode = z.infer<typeof StorefrontMode>;

/**
 * What a BAKERY may be (PRD OPS-013, CAT-015): one of the two shelves, never
 * `B2`.
 *
 * B2 says how a guest REACHES a mode B house, so a house can no more be B2 than
 * it can be a QR code. Every wire shape that types a bakery's own mode takes
 * this, which is what refuses `{ storefrontMode: 'B2' }` on a bakery create at
 * the boundary rather than in a handler.
 */
export const BakeryStorefrontMode = z.enum(['A', 'B']);
export type BakeryStorefrontMode = z.infer<typeof BakeryStorefrontMode>;

/** PRD GST-016. Immediate is deferred; the field exists so adding it is not a re-model. */
export const FulfillmentMode = z.enum(['PREORDER', 'IMMEDIATE']);
export type FulfillmentMode = z.infer<typeof FulfillmentMode>;

/** PRD GST-019. Stored on the order, drives the bakery stock-out flow (BAK-019). */
export const SubstitutionChoice = z.enum(['REPLACE_EQUIVALENT', 'REFUND_LINE']);
export type SubstitutionChoice = z.infer<typeof SubstitutionChoice>;

/*
 * There is no `HandoffPreference` any more (founder decision 2026-08-21).
 *
 * It was a closed set of three - ring first, safe drop, meet outside - that the
 * guest picked at checkout and the courier applied at the door. Every delivery
 * is handed over in person now, the way a food-delivery order is, so the set had
 * exactly one live member and the picker asked a question with one answer. A
 * guest who needs something particular says it in the delivery instructions,
 * which is free text, reaches the courier in the same brief, and can say the
 * thing an enum of three never could.
 *
 * `safeDropPolicy` went with it: a property-level permission to leave an order
 * at a door is unreachable once no order can be left at a door, and a setting
 * that cannot do anything is worse than no setting (PRD GST-007, RTE-013).
 */

/** PRD CHN-001. Recorded on every order from day one so later channels need no migration. */
export const AcquisitionChannel = z.enum(['PROPERTY_QR', 'PARTNER_LINK', 'BAKERY_STICKER']);
export type AcquisitionChannel = z.infer<typeof AcquisitionChannel>;

/**
 * PRD PAY-013. What an incident WAS where the order's own vocabulary has no
 * word for it - a door the guest could not be reached at (ACCESS_FAILURE, row
 * 7b), the bank's dispute, goodwill, the apartment removed. The domain's
 * shared `INCIDENT_CAUSES` tuple.
 *
 * The liability words BAKERY_ERROR, COURIER_FAILURE, PLATFORM_ERROR and
 * GUEST_CANCELLATION are RETIRED (founder, 5 October 2026): who bears an
 * ending is the case's suggested party (`suggestedChargedTo`), read off the
 * order's `OrderCancelledCause`, and a cancellation carries no incident cause.
 *
 * Adding a member widens the wire in ONE direction: nothing may emit a new
 * cause until the release carrying it has deployed everywhere that parses one,
 * and the database type must accept it before any row carries it.
 */
export const IncidentCause = z.enum(INCIDENT_CAUSES);
export type IncidentCause = z.infer<typeof IncidentCause>;

/**
 * Where an operator lands a courier job they declare over (issue #1471): the
 * three settled delivery states. The shared `DELIVERY_CLOSED_OUTCOMES`, in the order the
 * Close delivery select offers them.
 */
export const DeliveryClosedOutcome = z.enum(DELIVERY_CLOSED_OUTCOMES);
export type DeliveryClosedOutcome = z.infer<typeof DeliveryClosedOutcome>;

/**
 * The one party an operator holds responsible for a refund (founder decision,
 * 15 September 2026). Recorded with every refund decision beside the cause:
 * the cause says what happened, this says who answers for it.
 */
export const ResponsibleParty = z.enum(RESPONSIBLE_PARTIES);
export type ResponsibleParty = z.infer<typeof ResponsibleParty>;

/**
 * The line adjustments a counter makes that reduce what the guest is charged,
 * named exactly as the refund scope that records them (`RefundOrderInput`
 * in services): units removed from a line (BAK-019), units removed from one
 * part of a set (CAT-018), and a cheaper approved replacement (GST-020).
 */
export const RefundAdjustmentKind = z.enum(['LINE_UNITS', 'LINE_COMPONENT_UNITS', 'LINE_DELTA']);
export type RefundAdjustmentKind = z.infer<typeof RefundAdjustmentKind>;

/**
 * PRD BAK-003 / finding F-38. The closed set of rejection causes the tablet
 * forces a choice from - uses the shared `REJECTION_REASON_CODES` tuple. The code crosses the wire AS a code, on the reject request and on
 * the internal order view alike; a free-text note rides separately and never
 * replaces it. No member may contain a colon (the 0017/0018 backfill split on
 * the first ': ').
 */
export const RejectionReasonCode = z.enum(REJECTION_REASON_CODES);
export type RejectionReasonCode = z.infer<typeof RejectionReasonCode>;

/** Organization type. Conciergeries and independent owners share one entity (PRD section 7). */
export const OrganizationType = z.enum(['CONCIERGERIE', 'INDEPENDENT_OWNER', 'BAKERY', 'INTERNAL']);
export type OrganizationType = z.infer<typeof OrganizationType>;

/**
 * The two property-organization kinds - a conciergerie and an independent
 * owner - which share one entity (PRD section 7) and are what "partner" means
 * wherever a bakery is not offered: a property's owner, a rate card's client
 * type, a coverage owner. Named once so the pair is never typed out again.
 */
export const PARTNER_ORG_TYPES = ['CONCIERGERIE', 'INDEPENDENT_OWNER'] as const;
export const PartnerOrgType = OrganizationType.extract(PARTNER_ORG_TYPES);
export type PartnerOrgType = z.infer<typeof PartnerOrgType>;

/**
 * The internal portal's staff roles. A role reaches the worker as a string
 * (Clerk's membership role, the development header, or the finance-approver
 * allowlist minting `INTERNAL_FINANCE`), so `AuthContext.role` stays open; a
 * comparison against one names it as `InternalRole.enum.INTERNAL_FINANCE`, which
 * a misspelling cannot compile past.
 */
export const INTERNAL_ROLES = ['INTERNAL_ADMIN', 'INTERNAL_SUPPORT', 'INTERNAL_FINANCE'] as const;
export const InternalRole = z.enum(INTERNAL_ROLES);
export type InternalRole = z.infer<typeof InternalRole>;

/**
 * How a party stands with respect to VAT, which decides what document it gets
 * (plan `mode-a-menu-and-pricing` section 15).
 *
 * Three values, and the third is the point (founder decision, 2026-08-27). An
 * individual property owner may be a private individual rather than a
 * registered business: they earn the same commission and are paid the same way,
 * but they supply no commercial service, so there is no self-billed invoice to
 * issue them, no SIREN to print and no VAT to state. Two values would have made
 * a legitimate private individual and an unfinished onboarding the same row.
 */
export const VatStatus = z.enum(['REGISTERED', 'FRANCHISE_EN_BASE', 'PRIVATE_INDIVIDUAL']);
export type VatStatus = z.infer<typeof VatStatus>;

/**
 * What a payee is for tax purposes, as it answered "Sur le plan fiscal, vous
 * etes" (docs/plans/owner-onboarding.html section 4). A sole proprietor is ONE
 * person - an individual at the payment provider - yet has a SIREN and is
 * self-billed, which is why it is a value of its own.
 */
export const TaxKind = z.enum(['INDIVIDUAL', 'SOLE_PROPRIETOR', 'COMPANY']);
export type TaxKind = z.infer<typeof TaxKind>;

/** ISO 3166-1 alpha-2, upper case: `FR`, `BE`. */
export const CountryCode = z.string().regex(/^[A-Z]{2}$/);

/**
 * Why a conciergerie's or independent owner's commission carries no VAT, so a
 * zero VAT is never printed without its reason
 * (docs/finance/pricing-and-vat.html#partner-commission). The domain's
 * `commissionVatExemptionOf` decides it; null on the wire means VAT is charged.
 */
export const CommissionVatExemption = z.enum([
  'FRANCHISE_EN_BASE',
  'PRIVATE_INDIVIDUAL',
  'NOT_REGISTERED',
]);
export type CommissionVatExemption = z.infer<typeof CommissionVatExemption>;

/** PRD WEB-006. Payout onboarding is the real gate on self-serve. */
export const PayoutVerificationState = z.enum(['NOT_STARTED', 'PENDING', 'VERIFIED', 'RESTRICTED']);
export type PayoutVerificationState = z.infer<typeof PayoutVerificationState>;

/**
 * Why a baker or partner is Suspended (docs/finance/onboarding.html#suspension):
 * an operator's manual Suspend, an unfinished Stripe onboarding, or an unstated
 * VAT status - and nothing else. `suspensionReasons` derives it.
 */
export const SuspensionReason = z.enum(['MANUAL', 'ONBOARDING', 'VAT_STATUS']);
export type SuspensionReason = z.infer<typeof SuspensionReason>;

/** PRD BAK-005. */
export const ItemAvailability = z.enum(CATALOGUE_AVAILABILITIES);
export type ItemAvailability = z.infer<typeof ItemAvailability>;

/**
 * Why this counter stopped taking orders (PRD BAK-006, BAK-038).
 *
 * `MANUAL` is a human at the counter choosing to pause. The two `UNATTENDED_`
 * values are the platform pausing FOR the counter because nobody appears to be
 * standing at it - two orders expired unanswered in a row, or the tablet went
 * silent inside the pickup window. They are one enum rather than a boolean
 * because the counter is told which of the two happened and operations reads
 * the same word on the readiness panel.
 */
export const ServicePauseReason = z.enum(['MANUAL', 'UNATTENDED_EXPIRED', 'UNATTENDED_HEARTBEAT']);
export type ServicePauseReason = z.infer<typeof ServicePauseReason>;

/**
 * PRD BAK-002, and BAK-064 for the last one.
 *
 * `CANCELLED` holds an order this counter had ACCEPTED and somebody else
 * cancelled, for thirty seconds (`counterOwesCancellationNotice`, `CANCELLED_TILE_MS`).
 * Every other cancelled order is on no lane at all, as before.
 */
export const BakeryQueueName = z.enum([
  'NEW',
  'PREPARE_LATER',
  'PREPARE_NOW',
  'READY',
  'COLLECTED',
  'ISSUES',
  'CANCELLED',
]);
export type BakeryQueueName = z.infer<typeof BakeryQueueName>;

/** Provider-neutral delivery job status (PRD RTE-017). No provider string ever leaks here. */
export const DeliveryJobStatus = z.enum(DELIVERY_JOB_STATUSES);
export type DeliveryJobStatus = z.infer<typeof DeliveryJobStatus>;

/**
 * Adapter capability flags (PRD RTE-022). Core code branches on these, never on
 * provider identity. A missing capability degrades to a defined fallback and is
 * surfaced in the operations portal - it must never silently no-op.
 */
export const ProviderCapability = z.enum(PROVIDER_CAPABILITIES);
export type ProviderCapability = z.infer<typeof ProviderCapability>;

/** PRD OPS-009. Kill-switch scope. */
export const KillSwitchScope = z.enum(['GLOBAL', 'CITY', 'BAKERY', 'PROPERTY']);
export type KillSwitchScope = z.infer<typeof KillSwitchScope>;

/** PRD GST-038. Two distinct ratings, both skippable. */
export const RatingSubject = z.enum(['DELIVERY', 'PRODUCTS']);
export type RatingSubject = z.infer<typeof RatingSubject>;

export const AllergenCode = z.enum(ALLERGEN_CODES);
export type AllergenCode = z.infer<typeof AllergenCode>;

export const DietaryTag = z.enum(DIETARY_TAGS);
export type DietaryTag = z.infer<typeof DietaryTag>;

/** PRD WEB-017. Door position column of the bulk-import CSV. */
export const DoorPosition = z.enum(DOOR_POSITIONS);
export type DoorPosition = z.infer<typeof DoorPosition>;

/**
 * PRD OPS-001. Why the platform refused a guest BEFORE an order existed.
 *
 * Closed rather than open-string, unlike an operations alert's `kind`: this is
 * the axis the activity feed groups and counts on, so a producer inventing a
 * fifteenth spelling of "too late" would make a day's tile lie rather than
 * merely look unfamiliar. The domain store and this wire validator use the
 * same `CHECKOUT_REFUSAL_CAUSES` tuple.
 */
export const CheckoutRefusalCause = z.enum(CHECKOUT_REFUSAL_CAUSES);
export type CheckoutRefusalCause = z.infer<typeof CheckoutRefusalCause>;

/**
 * Every event the order machine can write, as the Activity feed reads them.
 *
 * The machine itself lives in `@croissants/domain`, which contracts cannot
 * import and the frontends do not depend on - so the vocabulary is stated here
 * and PINNED against the machine by `packages/services/src/order-events.test.ts`,
 * which imports both and fails on any divergence. That is the same shape the
 * reassign-eligibility projection already uses.
 *
 * It matters because `tCode` falls back to the raw code without complaining: an
 * event the catalogue cannot name prints `BAG_READY` at an operator mid-morning.
 *
 * ## A NAME LEAVES THIS LIST ONLY WITH ITS ROWS
 *
 * `order_events` is append-only and stores the type as TEXT, so a name the
 * machine has stopped writing is still a name the Activity feed, the timeline
 * and `countersForEvents` read on rows already in the table. Retiring an event
 * therefore means the machine stops producing it and this list keeps naming it
 * - which is why the parity suite in services checks that every event the
 * machine CAN write is here rather than that the two sets are equal, and names
 * the retired ones out loud. `REFUND_ISSUED`, `LIABILITY_REVIEW_OPENED` and
 * `LIABILITY_DECIDED` left this list anyway (register item 51): the rows that
 * hold them are development data (founder, 2026-09-22), the wire declares an
 * event's type as a free string, and the Activity feed titles a type it has no
 * label for with the type itself.
 */
export const ORDER_EVENT_TYPES = [
  'PAYMENT_INTENT_CREATED',
  'PAYMENT_AUTHORIZATION_FAILED',
  'PAYMENT_CAPTURED',
  'PAYMENT_CAPTURE_FAILED',
  'PAYMENT_VOIDED',
  /*
   * A dispute case decided with `chargedTo` COURIER opens the provider claim
   * on its own, without touching the payment region (thin refund cases,
   * 29 September 2026): the reference is the case id.
   */
  'REFUND_PROVIDER_CLAIM_OPENED',
  'BAKERY_ALLOCATED',
  'BAKERY_ACCEPTED',
  'BAKERY_REFUSED',
  /*
   * The two timer events of the offer interval (BAK-044): the clock crossing
   * `acceptanceOpensAt`, and the clock crossing `announceFromAt`. Raised by
   * `sweepAcceptance` and by no person, exactly as `ACCEPTANCE_LAPSED` below
   * already is - the mechanism is in production and already walked by the
   * coverage gate.
   */
  'ACCEPTANCE_OPENED',
  'ACCEPTANCE_ANNOUNCED',
  'ACCEPTANCE_LAPSED',
  'ALLOCATION_EXHAUSTED',
  'BAG_READY',
  /*
   * GitHub #1299: the counter answering for a bag the courier took before
   * anybody declared it ready. `COLLECTED -> COLLECTED` on the order region -
   * the rider has had the bag throughout - and a MOVE all the same, because
   * the state it lands on carries the answer.
   */
  'BAG_CONFIRMED',
  // An indicative estimate that prices the guest's windows, and - when the
  // network issued one with it - the bookable id the booking then redeems.
  'DELIVERY_ESTIMATED',
  /*
   * RETIRED, AND KEPT (founder, 15 September 2026, decision 13). The bookable
   * quote is taken and redeemed inside the booking write now, so the machine
   * writes neither of these again - and `order_events` holds the rows it wrote
   * before, which an operator opening a September order still reads.
   */
  'DELIVERY_QUOTED',
  'DELIVERY_QUOTE_EXPIRED',
  'COURIER_BOOKED',
  'PROVIDER_COURIER_ASSIGNED',
  // Ruling #9: the provider took the rider off the job and keeps the job.
  'PROVIDER_COURIER_WITHDRAWN',
  'PROVIDER_PICKED_UP',
  'PROVIDER_COURIER_IMMINENT',
  'PROVIDER_DELIVERED',
  'COURIER_UNAVAILABLE',
  'DELIVERY_FAILED',
  // The return leg (founder ruling #21): the bag rides back, then is back.
  'PROVIDER_RETURNING',
  'PROVIDER_RETURNED',
  /*
   * An operator ending a courier job no provider ended, on an order that has
   * already ended (issue #1471). The delivery region alone moves.
   */
  'DELIVERY_CLOSED_BY_OPERATIONS',
  'ORDER_CANCELLED',
  // Historical journal vocabulary. No active producer or machine edge remains.
  'ESCALATED_TO_MANUAL',
  'ORDER_CLOSED',
  // The provider claim, on its own clock and outliving the order (plan 1.4).
  'CLAIM_AMOUNT_SET',
  'CLAIM_SETTLED',
  'CLAIM_REJECTED',
  'CLAIM_ESCALATED',
  'CLAIM_WRITTEN_OFF',
] as const;
